import { React, useEffect, useState } from "react";
import CustomInput from "../../components/CustomInput";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Dropzone from "react-dropzone";
import { delImg, uploadImg } from "../../features/upload/uploadSlice";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  createContentBlock,
  getAContentBlock,
  resetState,
  updateAContentBlock,
} from "../../features/cms/contentBlocks/cmsContentBlocksSlice";

let schema = yup.object().shape({
  name: yup.string().required("Name is Required"),
  type: yup.string().required("Type is Required"),
});

const AddContentBlock = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const getBlockId = location.pathname.split("/")[4];
  const imgState = useSelector((state) => state?.upload?.images);
  const blockState = useSelector((state) => state.cmsContentBlocks);

  const {
    isSuccess,
    isError,
    isLoading,
    createdContentBlock,
    blockName,
    blockType,
    blockContent,
    blockHtmlContent,
    blockVideoUrl,
    blockIsActive,
    blockOrder,
    blockDisplayOn,
    blockConfig,
    blockImages,
    updatedContentBlock,
  } = blockState;

  const [displayOn, setDisplayOn] = useState([]);
  const [config, setConfig] = useState({});

  useEffect(() => {
    dispatch(resetState());
    if (!getBlockId) {
      setDisplayOn(["all"]);
      setConfig({});
    }
  }, []);

  useEffect(() => {
    if (getBlockId !== undefined) {
      dispatch(getAContentBlock(getBlockId));
    }
  }, [getBlockId]);

  useEffect(() => {
    if (blockDisplayOn) {
      setDisplayOn(Array.isArray(blockDisplayOn) ? blockDisplayOn : [blockDisplayOn]);
    }
    if (blockConfig) {
      setConfig(blockConfig);
    }
  }, [blockDisplayOn, blockConfig]);

  useEffect(() => {
    if (isSuccess && createdContentBlock) {
      toast.success("Content Block Added Successfully!");
      navigate("/admin/cms/content-blocks");
    }
    if (isSuccess && updatedContentBlock) {
      toast.success("Content Block Updated Successfully!");
      navigate("/admin/cms/content-blocks");
    }
    if (isError) {
      toast.error("Something Went Wrong!");
    }
  }, [isSuccess, isError, isLoading, navigate]);

  const img = [...(imgState || [])];
  if (blockImages && Array.isArray(blockImages)) {
    blockImages.forEach((i) => {
      if (!img.find((existing) => existing.public_id === i.public_id)) {
        img.push({
          public_id: i.public_id,
          url: i.url,
        });
      }
    });
  }

  const handleDisplayOnChange = (value) => {
    if (displayOn.includes(value)) {
      setDisplayOn(displayOn.filter((item) => item !== value));
    } else {
      setDisplayOn([...displayOn, value]);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: blockName || "",
      type: blockType || "",
      content: blockContent || "",
      htmlContent: blockHtmlContent || "",
      videoUrl: blockVideoUrl || "",
      isActive: blockIsActive !== undefined ? blockIsActive : true,
      order: blockOrder || 0,
      displayOn: displayOn,
      config: config,
      images: img || [],
    },
    validationSchema: schema,
    onSubmit: (values) => {
      const blockData = {
        ...values,
        displayOn: displayOn,
        config: config,
      };
      if (getBlockId !== undefined) {
        dispatch(updateAContentBlock({ id: getBlockId, blockData }));
        dispatch(resetState());
      } else {
        dispatch(createContentBlock(blockData));
        formik.resetForm();
        setTimeout(() => {
          dispatch(resetState());
        }, 300);
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue("displayOn", displayOn);
    formik.setFieldValue("config", config);
  }, [displayOn, config]);

  const renderTypeSpecificFields = () => {
    switch (formik.values.type) {
      case "banner":
        return (
          <>
            <div className="mt-3">
              <CustomInput
                type="text"
                label="Button Text"
                value={config.buttonText || ""}
                onChng={(e) =>
                  setConfig({ ...config, buttonText: e.target.value })
                }
              />
            </div>
            <div className="mt-3">
              <CustomInput
                type="text"
                label="Button URL"
                value={config.buttonUrl || ""}
                onChng={(e) =>
                  setConfig({ ...config, buttonUrl: e.target.value })
                }
              />
            </div>
            <div className="mt-3">
              <CustomInput
                type="text"
                label="Height"
                value={config.height || ""}
                onChng={(e) => setConfig({ ...config, height: e.target.value })}
              />
            </div>
            <div className="mt-3">
              <label className="mb-2">Text Align</label>
              <select
                className="form-control"
                value={config.textAlign || "center"}
                onChange={(e) =>
                  setConfig({ ...config, textAlign: e.target.value })
                }
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </>
        );
      case "text":
        return (
          <div className="mt-3">
            <label className="mb-2">Content</label>
            <ReactQuill
              theme="snow"
              value={formik.values.content}
              onChange={(value) => formik.setFieldValue("content", value)}
            />
          </div>
        );
      case "html":
        return (
          <div className="mt-3">
            <label className="mb-2">HTML Content</label>
            <textarea
              className="form-control"
              rows="10"
              value={formik.values.htmlContent}
              onChange={formik.handleChange("htmlContent")}
            />
          </div>
        );
      case "video":
        return (
          <div className="mt-3">
            <CustomInput
              type="text"
              label="Video URL"
              name="videoUrl"
              onChng={formik.handleChange("videoUrl")}
              val={formik.values.videoUrl}
            />
          </div>
        );
      case "product-carousel":
        return (
          <>
            <div className="mt-3">
              <CustomInput
                type="text"
                label="Category ID"
                value={config.categoryId || ""}
                onChng={(e) =>
                  setConfig({ ...config, categoryId: e.target.value })
                }
              />
            </div>
            <div className="mt-3">
              <CustomInput
                type="number"
                label="Limit"
                value={config.limit || 10}
                onChng={(e) =>
                  setConfig({ ...config, limit: parseInt(e.target.value) })
                }
              />
            </div>
            <div className="mt-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={config.autoplay || false}
                  onChange={(e) =>
                    setConfig({ ...config, autoplay: e.target.checked })
                  }
                />
                <label className="form-check-label">Autoplay</label>
              </div>
            </div>
            <div className="mt-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={config.showDots || false}
                  onChange={(e) =>
                    setConfig({ ...config, showDots: e.target.checked })
                  }
                />
                <label className="form-check-label">Show Dots</label>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <h3 className="mb-4 title">
        {getBlockId !== undefined ? "Edit" : "Add"} Content Block
      </h3>

      <div className="">
        <form action="" onSubmit={formik.handleSubmit}>
          <div className="mt-4">
            <CustomInput
              type="text"
              label="Block Name"
              name="name"
              onChng={formik.handleChange("name")}
              onBlr={formik.handleBlur("name")}
              val={formik.values.name}
            />
          </div>
          <div className="error">
            {formik.touched.name && formik.errors.name}
          </div>

          <div className="mt-3">
            <label className="mb-2">Type</label>
            <select
              name="type"
              onChange={formik.handleChange("type")}
              onBlur={formik.handleBlur("type")}
              value={formik.values.type}
              className="form-control py-3"
            >
              <option value="">Select Type</option>
              <option value="banner">Banner</option>
              <option value="text">Text</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="html">HTML</option>
              <option value="product-carousel">Product Carousel</option>
              <option value="testimonial">Testimonial</option>
              <option value="faq">FAQ</option>
            </select>
          </div>
          <div className="error">
            {formik.touched.type && formik.errors.type}
          </div>

          {renderTypeSpecificFields()}

          <div className="mt-3">
            <CustomInput
              type="number"
              label="Order"
              name="order"
              onChng={formik.handleChange("order")}
              val={formik.values.order}
            />
          </div>

          <div className="mt-3">
            <label className="mb-2">Display On</label>
            <div className="d-flex flex-wrap gap-3">
              {["all", "home", "product", "category", "cart", "checkout"].map(
                (option) => (
                  <div key={option} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={displayOn.includes(option)}
                      onChange={() => handleDisplayOnChange(option)}
                    />
                    <label className="form-check-label text-capitalize">
                      {option}
                    </label>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="mt-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="isActive"
                checked={formik.values.isActive}
                onChange={(e) =>
                  formik.setFieldValue("isActive", e.target.checked)
                }
              />
              <label className="form-check-label" htmlFor="isActive">
                Active
              </label>
            </div>
          </div>

          {(formik.values.type === "banner" ||
            formik.values.type === "image") && (
            <>
              <div className="bg-white border-1 p-5 text-center mt-3">
                <Dropzone
                  onDrop={(acceptedFiles) =>
                    dispatch(uploadImg(acceptedFiles))
                  }
                >
                  {({ getRootProps, getInputProps }) => (
                    <section>
                      <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <p>
                          Drag 'n' drop some files here, or click to select
                          files
                        </p>
                      </div>
                    </section>
                  )}
                </Dropzone>
              </div>
              <div className="showimages d-flex flex-wrap mt-3 gap-3">
                {img?.map((i, j) => {
                  return (
                    <div className=" position-relative" key={j}>
                      <button
                        type="button"
                        onClick={() => dispatch(delImg(i.public_id))}
                        className="btn-close position-absolute"
                        style={{ top: "10px", right: "10px" }}
                      ></button>
                      <img src={i.url} alt="" width={200} height={200} />
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <button
            className="btn btn-success border-0 rounded-3 my-5"
            type="submit"
          >
            {getBlockId !== undefined ? "Update" : "Add"} Content Block
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddContentBlock;

